import React, { useRef, useState } from 'react';
import { Button, message, Upload, Image, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import MessageConfirmModal from '../messageModal/MessageConfirmModal';
import UploadColorSvg from '../../assets/images/uploadColorSvg';
import { useTranslation } from 'react-i18next';
import ChangeCoverSvg from '../../assets/images/ChangeCoverSvg';
import { postDataRequest } from '../../api/serviceMethods';
import { UPLOAD_PUBLIC_URL } from '../../api/URLs';
import { RemoveSvg } from '../../assets/images/RemoveSvg';
import { formatFileSize, getFileType } from '../../utils/string';

const { Dragger } = Upload;

const DragUploadAdsSettingComponent = ({
    size = 1,
    fileList,
    setFileList,
    changeAvatarTitle = "",
    allowType = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"],
    titleallowFile="",
    customRequest,
    loading: externalLoading
}) => {
    const messageModalRef = useRef("");
    const { i18n, t } = useTranslation();

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const uploadRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const isSpinning = !!(loading || externalLoading);

    // Convert image file to base64
    const getBase64 = (img, callback) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    // Before upload validation
    const beforeUpload = (file) => {
        if (isSpinning) {
            return false;
        }
        const fileName = (file.name || "").toLowerCase();
        const ext = fileName.substring(fileName.lastIndexOf('.'));
        const fileType = (file.type || "").toLowerCase();

        const isAllowed = allowType.some(type => {
            const t = type.toLowerCase();
            if (t.startsWith('.')) {
                return ext === t;
            }
            if (t.includes('gif')) {
                return ext === '.gif' || fileType.includes('gif');
            }
            return fileType === t || ext === `.${t.split('/')[1]}`;
        });

        if (!isAllowed) {
            message.error(t('You cannot upload this file type! Only JPG, PNG, GIF formats are supported.'));
            return false;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error(t('Image must be smaller than 10MB!'));
            return false;
        }
        return true;
    };

    
    // Handle preview for image files
    const handlePreviewImage = async (file) => {
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    // Handle preview for image files
    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await new Promise((resolve) => {
                getBase64(file.originFileObj, resolve);
            });
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    // Custom upload logic using axios and FormData
    const handleCustomUpload = async (options) => {
        const { file, onSuccess, onError } = options;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);

            const response = await postDataRequest(UPLOAD_PUBLIC_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data', // Ensure the correct header for file upload
                },
            });

            // Check the response and use the fileUrl from the response
            if (response.fileUrl) {
                onSuccess(response);

                if (size === 1) {
                    setFileList([
                        {
                            uid: file.uid,
                            name: file.name,
                            status: 'done',
                            url: response.fileUrl,
                            size: file.size
                        }
                    ]);
                } else {
                    setFileList((prev) => [
                        ...(Array.isArray(prev) ? prev : []),
                        {
                            uid: file.uid,
                            name: file.name,
                            status: 'done',
                            url: response.fileUrl,
                            size: file.size
                        },
                    ]);
                }
                setLoading(false);

            } else {
                setLoading(false);
                throw new Error('Invalid response structure');
            }
        } catch (error) {
            setLoading(false);
            onError(error);
            message.error('Upload failed. Please try again.');
        }
    };

    // Handle file removal
    const handleRemove = (file) => {
        const updatedFileList = fileList.filter((item) => item.uid !== file.uid);
        setFileList(updatedFileList);
    };

    // Handle change for fileList
    const handleChange = (info) => {
        if (info.file.status === 'done') {
            // message.success(`${info.file.name} file uploaded successfully.`);
        } else if (info.file.status === 'error') {
            // message.error(`${info.file.name} file upload failed.`);
        }
    };

    // Change cover handler
    const handleChangeCover = () => {
        if (isSpinning) return;
        uploadRef.current.click();
    };

    const props = {
        disabled: isSpinning,
        beforeUpload,
        customRequest: customRequest || handleCustomUpload,
        multiple: size > 1,
        onChange: handleChange,
        onDrop: (e) => {
            if (isSpinning) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            console.log("Dropped files", e.dataTransfer.files);
        },
        onPreview: handlePreview,
        className: 'newUpload',
        showUploadList: false,
        onRemove: handleRemove,
        fileList: fileList
    };
    const hasValidFile = fileList && fileList.some(file => file.url && file.url.trim() !== "");


    return (
        <>
            <div className={`w-100 ${hasValidFile && size === 1 ? 'd-none' : ''}`}>
                <Dragger
                    {...props}
                    disabled={isSpinning}
                    style={{
                        pointerEvents: isSpinning ? 'none' : 'auto',
                        cursor: isSpinning ? 'not-allowed' : 'pointer'
                    }}
                >
                    <Spin spinning={isSpinning}>
                        <>
                            {
                                size > 1 && fileList && fileList.length > 0 ? (
                                    <div className='w-100 mb-2' onClick={(e) => e.stopPropagation()}>
                                        {fileList.map((it, i) => (
                                            <div 
                                                key={it.uid || it.url || i}
                                                className='d-flex mt-1 justify-content-between align-items-center ps-4 pe-4 pt-3 pb-3 mb-2' 
                                                style={{ background: "rgb(0 0 0 / 2%)", borderRadius: "8px" }}
                                            >
                                                <div className='d-flex align-items-center'>
                                                    <div className='pe-3'>
                                                        <img 
                                                            style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }} 
                                                            src={`${it.url}`} 
                                                            alt={it.name || "Banner"}
                                                        />
                                                    </div>
                                                    <div className='text-image'>
                                                        <div style={{ textAlign: "left", fontWeight: 500 }}>{it.name || 'NA'}</div>
                                                        <div style={{ textAlign: "left", color: "#6B7280" }}>
                                                            <span className='text-uppercase text-wrap-nowrap'>{it.url && getFileType(it.url)}</span>
                                                            <span className='ps-2' style={{ paddingBottom: "6px" }}>•</span>
                                                            <span className='ps-2'>{it.size ? formatFileSize(it.size) : `0 MB`}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <span 
                                                        className='remove-icon' 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            if (isSpinning) return;
                                                            const nextList = [...fileList];
                                                            nextList.splice(i, 1);
                                                            setFileList(nextList);
                                                        }} 
                                                        style={{ cursor: isSpinning ? "not-allowed" : "pointer" }}
                                                    >
                                                        <RemoveSvg />
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : ''
                            }
                            {(!hasValidFile || size > 1) && (
                                size > 1 && fileList && fileList.length > 0 ? (
                                    <div
                                        className="d-flex align-items-center justify-content-center mt-2 px-3 py-2"
                                        style={{
                                            minHeight: "44px",
                                            border: "1px dashed #D1D5DB",
                                            borderRadius: "8px",
                                            backgroundColor: isSpinning ? "#F3F4F6" : "#F9FAFB",
                                            cursor: isSpinning ? "not-allowed" : "pointer",
                                            pointerEvents: isSpinning ? "none" : "auto",
                                            opacity: isSpinning ? 0.6 : 1,
                                            transition: "all 0.2s ease",
                                            gap: "8px"
                                        }}
                                        onMouseEnter={(e) => {
                                            if (isSpinning) return;
                                            e.currentTarget.style.borderColor = "#9CA3AF";
                                            e.currentTarget.style.backgroundColor = "#F3F4F6";
                                        }}
                                        onMouseLeave={(e) => {
                                            if (isSpinning) return;
                                            e.currentTarget.style.borderColor = "#D1D5DB";
                                            e.currentTarget.style.backgroundColor = "#F9FAFB";
                                        }}
                                    >
                                        <PlusOutlined style={{ fontSize: "13px", color: "#6B7280" }} />
                                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
                                            {t("Add more images")}
                                        </span>
                                        <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
                                            ({t("Drag files here or")} <span style={{ textDecoration: "underline", color: isSpinning ? "#9CA3AF" : "#E50A47" }}>{t("Select File")}</span>)
                                        </span>
                                    </div>
                                ) : (
                                    <div>
                                        <Button
                                            disabled={isSpinning}
                                            style={{
                                                height: "48px",
                                                width: "48px",
                                                backgroundColor: "#F5F7F9",
                                                border: "none",
                                                cursor: isSpinning ? "not-allowed" : "pointer"
                                            }}
                                            ref={uploadRef}
                                        >
                                            <UploadColorSvg />
                                        </Button>
                                        <div className='mt-2'>
                                            <p className="ant-upload-text mt-2">
                                                {t("Drag files here or")}
                                                <a href="#!" style={{ marginLeft: "5px", textDecoration: "underline", pointerEvents: isSpinning ? "none" : "auto" }}>{t("Select File")}</a>
                                            </p>
                                            <p className="ant-upload-hint">
                                                {titleallowFile ? titleallowFile : t(`Support png/jpg/gif and other formats, the maximum file size is 10MB`)}
                                            </p>
                                        </div>
                                    </div>
                                )
                            )}
                        </>
                    </Spin>
                </Dragger>
            </div>

            {hasValidFile && size === 1 ? <div className='ant-upload-wrapper-cus w-100'>
                <div className='ant-upload ant-upload-drag align-content-center pe-3 ps-3 pt-4 pb-4'>
                    {
                        fileList && fileList.length > 0 ? fileList.map((it, i) => (
                            <div className='d-flex mt-1 mt-1 justify-content-between align-items-center ps-4 pe-4 pt-4 pb-4' style={{background: "rgb(0 0 0 / 2%)", borderRadius: "8px"}}>
                                <div className='d-flex align-items-center'>
                                    <div className='pe-3'>
                                        <img onClick={(() => handlePreviewImage(it) )} style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }} src={`${it.url}`} />
                                    </div>
                                    <div className='text-image'>
                                        <div style={{textAlign: "left"}}>{it.name || 'NA'}</div>
                                        <div style={{textAlign: "left"}}>
                                            <span className='text-uppercase text-wrap-nowrap'>{it.url && getFileType(it.url)}</span>
                                            <span className='ps-2' style={{paddingBottom: "6px"}}>.</span>
                                            <span className='ps-2'>{it.size ? formatFileSize(it.size) : `0 MB`}</span>
                                            
                                            </div>
                                    </div>
                                </div>
                                <div>
                                    <span onClick={() => {
                                        const nextList = [...fileList];
                                        nextList.splice(i, 1);
                                        setFileList(nextList);
                                    }} style={{cursor: "pointer"}}><RemoveSvg /></span>
                                </div>
                            </div>
                        )) : ''
                    }
                </div>
            </div> : ''}

            {hasValidFile && (
                <div className="mt-3 d-none text-center d-flex align-items-center">
                    <ChangeCoverSvg className="me-3" />
                    <a
                        href="#!"
                        onClick={handleChangeCover}
                        style={{ marginLeft: '10px' }}
                    >
                        <span className="span-danger">*</span> {changeAvatarTitle ? changeAvatarTitle : t('Change avatar')}
                    </a>
                </div>
            )}
            {previewImage && (
                <Image
                    wrapperStyle={{
                        display: 'none',
                    }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) =>
                            !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
            <MessageConfirmModal textCentered ref={messageModalRef} />
        </>
    );
};

export default DragUploadAdsSettingComponent;
